"use client"

import { TARGET_ELEMENT_SCROLL_OFFSET } from "@/assets/static/constants";
import { useBodyCentral } from "@/context/BodyCentralContext";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { usePathname } from "next/navigation";
import { ReactNode, useEffect, useState } from "react";
import styles from "./body_wrapper.module.scss";

interface Props {
    children: ReactNode
}

export default function BodyWrapper({ children }: Props) {
    const pathname = usePathname();
    const { scrollable } = useBodyCentral()
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        window.scrollTo({
            behavior: "instant",
            left: 0,
            top: 0
        });
    }, [pathname]);

    // Shine effect handler
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

            const findTargetElementFromHash = (hash: string): HTMLElement | null => {
                const id = hash.startsWith('#') ? hash.slice(1) : hash
                if (!id) return null

                const direct = document.getElementById(id)
                if (direct) return direct

                try {
                    const decoded = decodeURIComponent(id)
                    return document.getElementById(decoded)
                } catch {
                    return null
                }
            }

            const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false

            const waitForScrollTo = (targetY: number, timeoutMs: number, signal: AbortSignal): Promise<void> => {
                return new Promise((resolve) => {
                    const start = performance.now()
                    let lastY = window.scrollY
                    let lastMovementAt = start
                    let rafId = 0

                    const finish = () => {
                        if (rafId) cancelAnimationFrame(rafId)
                        resolve()
                    }

                    const tick = (now: number) => {
                        if (signal.aborted) return finish()

                        const y = window.scrollY
                        if (Math.abs(y - lastY) > 0.5) {
                            lastY = y
                            lastMovementAt = now
                        }

                        const closeEnough = Math.abs(y - targetY) <= 2
                        const stableLongEnough = now - lastMovementAt >= 120
                        if (closeEnough && stableLongEnough) return finish()
                        if (now - start >= timeoutMs) return finish()

                        rafId = requestAnimationFrame(tick)
                    }

                    rafId = requestAnimationFrame(tick)
                })
            }

            const createShineEffect = (targetElement: HTMLElement) => {
                const rect = targetElement.getBoundingClientRect()

                // Define a padding value to inset the shine effect
                const padding = 10

                const effectDiv = document.createElement('div')
                effectDiv.className = styles.shine_effect

                // Apply border-radius from computed style
                const computedStyle = window.getComputedStyle(targetElement)
                effectDiv.style.borderRadius = computedStyle.borderRadius

                effectDiv.style.top = rect.top + window.scrollY + padding + 'px'
                effectDiv.style.left = rect.left + window.scrollX + padding + 'px'
                effectDiv.style.width = rect.width - 2 * padding + 'px'
                effectDiv.style.height = rect.height - 2 * padding + 'px'

                document.body.appendChild(effectDiv)

                // Next tick ensures initial styles are committed before animation class
                requestAnimationFrame(() => {
                    effectDiv.classList.add(styles.shine)
                })

                effectDiv.addEventListener('animationend', () => effectDiv.remove())

                // Scale up the target element super smoothly
                targetElement.classList.add(styles.target_scale)
                targetElement.addEventListener('animationend', () => {
                    targetElement.classList.remove(styles.target_scale)
                }, { once: true })
            }

            let activeController: AbortController | null = null

            const handleAnchorClick = async (event: MouseEvent) => {
                if (event.defaultPrevented) return

                // Respect modifier keys / non-left-clicks so normal browser behaviors keep working
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

                const target = event.target
                if (!(target instanceof HTMLElement)) return

                // Use 'closest' to ensure we get the anchor element even if a child was clicked
                const anchor = target.closest('a')
                if (!(anchor instanceof HTMLAnchorElement)) return
                if (!anchor.hash) return
                if (anchor.target && anchor.target !== '_self') return
                if (anchor.hasAttribute('download')) return

                // Parse the anchor href to check if it targets the current page
                const anchorUrl = new URL(anchor.href, window.location.origin)
                const isSamePage = anchorUrl.pathname === pathname

                if (!isSamePage) {
                    return // Don't apply shine effect for cross-page anchors
                }

                const targetElement = findTargetElementFromHash(anchor.hash)
                if (!targetElement) return

                event.preventDefault()

                // Cancel any in-flight scroll/effect from prior clicks
                activeController?.abort()
                activeController = new AbortController()
                const { signal } = activeController

                const desiredTop =
                    targetElement.getBoundingClientRect().top + window.scrollY - TARGET_ELEMENT_SCROLL_OFFSET
                const maxScrollTop = document.documentElement.scrollHeight - window.innerHeight
                const targetTop = clamp(desiredTop, 0, Math.max(0, maxScrollTop))

                const startY = window.scrollY
                const distance = Math.abs(startY - targetTop)

                // Distance-based expected duration (ms). Used only as an upper bound.
                // ~2px/ms => 2000px/s, clamped to avoid being too short/long.
                const estimatedDurationMs = prefersReducedMotion ? 0 : clamp(distance / 2, 250, 3500)
                const timeoutMs = prefersReducedMotion ? 0 : estimatedDurationMs + 1200

                window.scrollTo({
                    top: targetTop,
                    behavior: prefersReducedMotion ? 'auto' : 'smooth',
                })

                if (!prefersReducedMotion) {
                    await waitForScrollTo(targetTop, timeoutMs, signal)
                }

                if (signal.aborted) return

                // Keep the URL hash in sync without triggering an extra native jump
                try {
                    history.pushState(null, '', anchor.hash)
                } catch {
                    // ignore
                }

                createShineEffect(targetElement)
            }

            document.body.addEventListener('click', handleAnchorClick, true)
            return () => {
                activeController?.abort()
                document.body.removeEventListener('click', handleAnchorClick, true)
            }
        }
    }, [pathname])

    useScrollAnimation();

    return (
        <body style={{ overflow: scrollable && mounted ? 'auto' : 'hidden' }}>
            {children}
        </body>
    )
}